import React from "react";
import Variant from "shared/enums/Variant";
import { ProgramParticipantTag } from "shared/enums/ProgramParticipantTag";
import { useAddMeetingRepTag, useRemoveMeetingRepTag } from "./dataloader";
import {
  ApiProgramParticipantAddMeetingRepTagRequest,
  ApiProgramParticipantRemoveMeetingRepTagRequest,
  ManageMeetingRepCellProps
} from "./types";
import { useCopies } from "shared/hooks/useCopies";
import { useCommonFeatures } from "context/commonFeatures";

export default function useManageMeetingRepCell(props: ManageMeetingRepCellProps) {
  const copies = useCopies();
  const { initConfirmationAlert } = useCommonFeatures();

  const { mutateAsync: addMeetingRepTag } = useAddMeetingRepTag();
  const { mutateAsync: removeMeetingRepTag } = useRemoveMeetingRepTag();

  const isMeetingRep = React.useMemo(
    () => props.row.original.tags?.includes(ProgramParticipantTag.MEETING_REP),
    [props.row.original.tags]
  );

  const isMeetingRepTagChangeDisabled = React.useMemo(
    () =>
      !props.meetingCapInitialized ||
      (!isMeetingRep && !props.meetingRepTagAddAllowedAsPerProgramState) ||
      (isMeetingRep && !props.meetingRepTagRemoveAllowedAsPerProgramState),
    [
      isMeetingRep,
      props.meetingCapInitialized,
      props.meetingRepTagAddAllowedAsPerProgramState,
      props.meetingRepTagRemoveAllowedAsPerProgramState
    ]
  );

  const addMeetingRepTagHandler = React.useCallback(() => {
    initConfirmationAlert({
      message: (
        <span
          dangerouslySetInnerHTML={{
            __html: props.isLoggedInUser
              ? copies.get("meeting.rep.tag.add.for.self.alert")
              : copies.get("meeting.rep.tag.add.alert", {
                  0: `${props.row.original.firstName} ${props.row.original.lastName}`,
                  1: props.row.original.firstName
                })
          }}
        />
      ),
      primaryButtonVariant: Variant.SUCCESS,
      handleConfirm: () => {
        const addMeetingRepPayload: ApiProgramParticipantAddMeetingRepTagRequest = {
          programParticipantId: props.row.original.id,
          eventParticipantId: props.row.original.eventParticipantId,
          programId: props.programId,
          uiVersion: props.row.original.uiVersion
        };
        addMeetingRepTag(addMeetingRepPayload).then(data => {
          props.manageChangeHandler({
            programParticipantId: data.id,
            uiVersion: data.uiVersion
          });
        });
        return true;
      }
    });
  }, [addMeetingRepTag, initConfirmationAlert, copies, props]);

  const removeMeetingRepTagHandler = React.useCallback(() => {
    initConfirmationAlert({
      message: (
        <span
          dangerouslySetInnerHTML={{
            __html: props.isLoggedInUser
              ? copies.get("meeting.rep.tag.remove.for.self.alert")
              : copies.get("meeting.rep.tag.remove.alert", {
                  0: `${props.row.original.firstName} ${props.row.original.lastName}`,
                  1: props.row.original.firstName
                })
          }}
        />
      ),
      handleConfirm: () => {
        const removeMeetingRepPayload: ApiProgramParticipantRemoveMeetingRepTagRequest = {
          id: props.row.original.id,
          uiVersion: props.row.original.uiVersion
        };
        removeMeetingRepTag(removeMeetingRepPayload).then(data => {
          props.manageChangeHandler({
            programParticipantId: data.id,
            uiVersion: data.uiVersion
          });
        });
        return true;
      }
    });
  }, [initConfirmationAlert, copies, props, removeMeetingRepTag]);

  const meetingRepChangeHandler = React.useCallback(() => {
    if (isMeetingRep) {
      removeMeetingRepTagHandler();
    } else {
      addMeetingRepTagHandler();
    }
  }, [addMeetingRepTagHandler, isMeetingRep, removeMeetingRepTagHandler]);

  return {
    isMeetingRep,
    meetingRepChangeHandler,
    isMeetingRepTagChangeDisabled
  };
}
